// apps/workflow/src/pages/designer/Earnings.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  RefreshCw,
  Search,
  Filter,
  Calendar,
  User,
  FileText,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

// ─── Helpers ──────────────────────────────────────────────────────
const formatCurrency = (amount) => {
  if (amount == null) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const getStatusBadge = (status) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800' },
    credited: { label: 'Credited', color: 'bg-green-100 text-green-800' },
  };
  return config[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-800' };
};

// ─── Main Component ──────────────────────────────────────────────
const DesignerEarnings = () => {
  const { user } = useAuth();
  const userRole = user?.role || 'designer';
  const isAdmin = ['admin', 'super_admin', 'manager'].includes(userRole);
  const isFreelancer = userRole === 'freelancer';

  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedDesigner, setSelectedDesigner] = useState(isFreelancer ? user?.id : '');
  const [designers, setDesigners] = useState([]);

  // ─── Fetch designers (if admin) ──────────────────────────────
  useEffect(() => {
    if (isAdmin) {
      const fetchDesigners = async () => {
        try {
          const users = await pb.collection('users').getFullList({
            filter: 'role = "freelancer" || role = "designer"',
            sort: 'name',
            requestKey: null,
          });
          setDesigners(users);
          if (!selectedDesigner && users.length > 0) {
            setSelectedDesigner(users[0].id);
          }
        } catch (error) {
          console.error('Error fetching designers:', error);
        }
      };
      fetchDesigners();
    }
  }, [isAdmin]);

  // ─── Fetch earnings ──────────────────────────────────────────
  const fetchEarnings = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      let filter = '';
      if (isFreelancer) {
        filter = `freelancer = "${user.id}"`;
      } else if (isAdmin && selectedDesigner) {
        filter = `freelancer = "${selectedDesigner}"`;
      } else {
        // For other roles, show nothing or all? We'll show empty.
        setEarnings([]);
        setLoading(false);
        return;
      }

      const records = await pb.collection('freelancer_earnings').getFullList({
        filter: filter,
        sort: '-created',
        expand: 'task, task.project',
        requestKey: null,
      });
      setEarnings(records);
    } catch (error) {
      if (!error.isAbort) {
        console.error('Error fetching earnings:', error);
        toast.error('Failed to load earnings');
      }
    } finally {
      setLoading(false);
    }
  }, [user, isFreelancer, isAdmin, selectedDesigner]);

  useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchEarnings();
    setRefreshing(false);
    toast.success('Earnings refreshed');
  };

  // ─── Filtering ──────────────────────────────────────────────
  const filteredEarnings = useMemo(() => {
    let list = earnings;

    if (searchTerm) {
      list = list.filter((e) =>
        e.expand?.task?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.expand?.task?.expand?.project?.projectName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      list = list.filter((e) => e.status === filterStatus);
    }

    return list;
  }, [earnings, searchTerm, filterStatus]);

  // ─── Stats ──────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = earnings.reduce((sum, e) => sum + (e.amount || 0), 0);
    const pending = earnings.filter((e) => e.status === 'pending').reduce((sum, e) => sum + (e.amount || 0), 0);
    const credited = earnings.filter((e) => e.status === 'credited').reduce((sum, e) => sum + (e.amount || 0), 0);
    const count = earnings.length;
    const creditedCount = earnings.filter((e) => e.status === 'credited').length;
    return { total, pending, credited, count, creditedCount };
  }, [earnings]);

  // ─── Loading ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading earnings...</p>
      </div>
    );
  }

  const designerName = isFreelancer
    ? user?.name
    : designers.find((d) => d.id === selectedDesigner)?.name || 'Designer';

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-600" />
            {isAdmin ? `Earnings – ${designerName || 'All'}` : 'My Earnings'}
          </h1>
          <p className="text-sm text-slate-500">
            {isFreelancer
              ? 'Track your earnings from completed tasks'
              : 'Manage earnings for designers and freelancers'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <select
              value={selectedDesigner}
              onChange={(e) => setSelectedDesigner(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
            >
              <option value="">Select Designer</option>
              {designers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.email})
                </option>
              ))}
            </select>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-slate-400 hover:text-indigo-600 transition rounded-lg hover:bg-indigo-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          title="Total Earned"
          value={formatCurrency(stats.total)}
          icon={DollarSign}
          color="emerald"
        />
        <StatCard
          title="Pending"
          value={formatCurrency(stats.pending)}
          icon={Clock}
          color="amber"
        />
        <StatCard
          title="Credited"
          value={formatCurrency(stats.credited)}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Tasks Completed"
          value={stats.creditedCount}
          icon={TrendingUp}
          color="indigo"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-3 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-1 min-w-[150px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by task or project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="credited">Credited</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {filteredEarnings.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">No earnings records found.</p>
            {isFreelancer && (
              <p className="text-xs text-slate-400 mt-1">
                Complete tasks to earn. Your earnings will appear here.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Task
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Credited On
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEarnings.map((earning) => {
                  const status = getStatusBadge(earning.status);
                  const task = earning.expand?.task;
                  const project = task?.expand?.project;
                  return (
                    <tr key={earning.id} className="hover:bg-slate-50/50 transition">
                      <td className="px-6 py-3 font-medium text-slate-800">
                        {task?.title || 'Deleted Task'}
                      </td>
                      <td className="px-6 py-3 text-slate-600">
                        {project?.projectName || 'N/A'}
                      </td>
                      <td className="px-6 py-3 font-semibold text-slate-800">
                        {formatCurrency(earning.amount)}
                      </td>
                      <td className="px-6 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-slate-500">
                        {earning.creditedAt ? formatDate(earning.creditedAt) : '—'}
                      </td>
                      <td className="px-6 py-3">
                        {task && (
                          <button
                            onClick={() => window.open(`/designer/task/${task.id}`, '_blank')}
                            className="text-indigo-600 hover:text-indigo-800 transition"
                            title="View Task"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── StatCard ──────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${colors[color] || 'bg-slate-50 text-slate-600'}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

export default DesignerEarnings;